/**
 * src/components/games/launchers/TosiosLauncher.tsx
 *
 * TOSIOS — The Open-Source IO Shooter (MIT, github.com/halftheopposite/TOSIOS).
 * A 2D top-down arena shooter that runs entirely in the browser: PIXI.js
 * client + Colyseus authoritative server (Express + Colyseus on the same
 * port). Everyone in the voice channel iframes the SAME TOSIOS lobby URL,
 * so they all see the same live room list; the host creates a room (name
 * it after the channel) and peers click it to join.
 *
 * Server configuration
 * --------------------
 * Run one TOSIOS server per deployment (not per VC — the room list IS the
 * co-location mechanism; up to 16 players per room):
 *
 *   npm run tosios:server            # Docker image halftheopposite/tosios, :3001
 *
 * Then point the arcade tile at it with the build-time env var:
 *
 *   VITE_TOSIOS_URL=http://<host>:3001   (default http://localhost:3001)
 *
 * For a one-click "everyone lands in the same match" deep-link instead of
 * the lobby flow, apply the optional auto-join patch and rebuild the
 * client from source — see scripts/vendor-tosios.mjs --patch and
 * scripts/run-tosios-server.mjs. With the patch, set VITE_TOSIOS_URL to the
 * `/new?autojoin=1&...` URL (autojoin + roomName + roomMaxPlayers +
 * roomMap + mode) and every player who opens it joins the same room
 * automatically (Colyseus 0.14 joinOrCreate matches by room type — TOSIOS
 * registers no filterBy). Without autojoin=1 the stock "New Game" button
 * keeps its always-create behavior, so the lobby flow stays intact.
 *
 * Multiplayer model: `multiplayer: true` + `solo: true` mirrors Quake 3 —
 * the framework's socket.io is ONLY the arcade host/request coordination
 * layer; the actual game network is TOSIOS's own Colyseus WebSocket
 * (ws://<tosios-host>:3001, same port as the HTTP lobby). "Play Solo"
 * mounts the iframe without a socket.io broadcast so a player can still
 * reach the server (e.g. to practice against the 3 monsters).
 */

import { Target } from 'lucide-react';
import { createIframeGame } from './IframeGameActivity';

const TOSIOS_URL =
  (import.meta.env.VITE_TOSIOS_URL as string | undefined) ?? 'http://localhost:3001';

export default createIframeGame({
  id: 'tosios',
  displayName: 'TOSIOS',
  shortDescription:
    '2D top-down arena shooter — everyone in the call joins the same lobby & room.',
  leaderboard: null,
  // Multiplayer is handled INSIDE the iframe (Colyseus WebSocket → the
  // TOSIOS server). The framework's socket.io flow is only the arcade
  // coordination layer — same pattern as Quake 3.
  multiplayer: true,
  multiplayerShape: {
    maxPlayers: 16,
    // TOSIOS's real transport is Colyseus (its own WebSocket protocol).
    // Advisory metadata for the arcade — the framework's
    // GameActivityManager was never built, so this is not enforced.
    transport: 'colyseus',
    requiresVoiceChannel: true,
    hostMigration: 'auto',
  },
  solo: true,
  url: TOSIOS_URL,
  icon: Target,
  attribution: {
    text:
      'TOSIOS — MIT — github.com/halftheopposite/TOSIOS  ·  Colyseus (MIT)  ·  PIXI.js (MIT)',
    license: 'MIT',
    href: 'https://github.com/halftheopposite/TOSIOS',
  },
});
